import useForm, { type UseFormReturnType } from 'hooks/use-form.hook';
import type { VcsApi } from '@env0/vcs-service/api';
import type { SchemaOf } from 'yup';
import * as Yup from 'yup';
import { useCallback, useMemo } from 'react';
import type { VcsConnection } from 'components/organizations/vcs/vcs-connection-modal/vcs-connection-modal-picker';
import { useCreateVcsConnection, useUpdateVcsConnection } from 'stores/rq/vcs-connections';
import { DEFAULT_AGENT_ID } from 'constants/agents.constants';
import { useDropSaasDefaultAgentKey } from 'hooks/use-drop-saas-default-agent-key.hook';
import type { UseFormMethods } from 'react-hook-form/dist/types';
import { getAvailableAuthenticationTypes } from 'components/organizations/vcs/vcs-connection-modal/access-card/supported-vcs-connections-common';

export type AuthenticationType = 'VcsAgent' | 'OAuth' | 'AccessToken';

export type VcsModalFormValues = Pick<
  VcsApi.VcsConnectionPayload,
  'url' | 'vcsAgentKey' | 'authorizationId' | 'tokenId'
> & {
  authenticationType: AuthenticationType;
};

export type VcsConnectionsForm = {
  vcsConnection: VcsConnection;
  form: UseFormReturnType<VcsModalFormValues>;
  onTokenProcessingChange?: (isProcessing: boolean) => void;
};

const vcsConnectionModalFormSchema: SchemaOf<VcsModalFormValues> = Yup.object({
  authenticationType: Yup.mixed<AuthenticationType>().required(),
  url: Yup.string()
    .optional()
    .when('authenticationType', {
      is: 'VcsAgent',
      then: Yup.string().required('validation.required')
    }),
  vcsAgentKey: Yup.string()
    .optional()
    .when('authenticationType', {
      is: 'VcsAgent',
      then: Yup.string().required('organization.settings.agents.vcs-connections-modal.agent-key.saas-default')
    }),
  authorizationId: Yup.string().optional(),
  tokenId: Yup.string().optional()
}).test('oauth-or-access-token', 'validation.required', values => {
  if (values.authenticationType === 'OAuth') return !!values.authorizationId || !!values.tokenId;
  if (values.authenticationType === 'AccessToken') return !!values.tokenId;
  return true;
});

export const useVcsConnectionModalForm = ({
  vcsConnection,
  onSubmit
}: {
  vcsConnection: VcsConnection;
  onSubmit: (result: any) => void;
}) => {
  const { mutateAsync: updateVcsConnection } = useUpdateVcsConnection();
  const { mutateAsync: createVcsConnection } = useCreateVcsConnection();

  const saveVcsConnection = useCallback(
    async (data: VcsModalFormValues, form: UseFormMethods<VcsModalFormValues>) => {
      let result;

      if (vcsConnection?.id) {
        const changes = form.getValues(Object.keys(form.formState.dirtyFields) as any);
        result = await updateVcsConnection({ id: vcsConnection.id, data: changes });
      } else {
        result = await createVcsConnection({ ...vcsConnection, ...data });
      }

      onSubmit(result);
    },
    [vcsConnection, onSubmit, updateVcsConnection, createVcsConnection]
  );

  const authenticationTypes = useMemo(() => getAvailableAuthenticationTypes(vcsConnection), [vcsConnection]);
  const form = useForm<VcsModalFormValues>({
    initialValues: {
      authenticationType: authenticationTypes[0],
      ...vcsConnection,
      vcsAgentKey: vcsConnection?.vcsAgentKey || DEFAULT_AGENT_ID
    },
    schema: vcsConnectionModalFormSchema,
    onSubmit: saveVcsConnection
  });

  useDropSaasDefaultAgentKey(form, vcsConnection);

  return { form };
};
